import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SortState } from '@/hooks/use-url-state';
import {
  approve_product,
  fetch_approval_counts,
  fetch_product_detail,
  fetch_product_queue,
  override_commission,
  reject_product,
} from './api';
import type { QueueFilters, Vertical } from './types';

const QUEUE_KEY = 'admin-product-queue';
const COUNTS_KEY = 'admin-product-counts';
const DETAIL_KEY = 'admin-product-detail';

export function use_product_queue(filters: QueueFilters, sort?: SortState) {
  return useQuery({
    queryKey: [QUEUE_KEY, filters, sort],
    queryFn: () => fetch_product_queue(filters, sort),
    placeholderData: (prev) => prev,
  });
}

export function use_approval_counts() {
  return useQuery({ queryKey: [COUNTS_KEY], queryFn: fetch_approval_counts });
}

/** Every list, tile and open record moves when a decision lands. */
function invalidate_all(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: [QUEUE_KEY] });
  qc.invalidateQueries({ queryKey: [COUNTS_KEY] });
  qc.invalidateQueries({ queryKey: [DETAIL_KEY] });
}

export function use_approve_product() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vertical, id, note }: { vertical: Vertical; id: string; note?: string }) =>
      approve_product(vertical, id, note),
    onSuccess: () => invalidate_all(qc),
  });
}

export function use_reject_product() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vertical, id, reason }: { vertical: Vertical; id: string; reason: string }) =>
      reject_product(vertical, id, reason),
    onSuccess: () => invalidate_all(qc),
  });
}

export function use_product_detail(vertical: Vertical | undefined, id: string | undefined) {
  return useQuery({
    queryKey: [DETAIL_KEY, vertical, id],
    queryFn: () => fetch_product_detail(vertical as Vertical, id as string),
    enabled: !!vertical && !!id,
  });
}

export function use_override_commission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: override_commission,
    onSuccess: () => {
      // Both, because a fee change moves the prices on the detail AND the queue row.
      qc.invalidateQueries({ queryKey: [DETAIL_KEY] });
      qc.invalidateQueries({ queryKey: [QUEUE_KEY] });
    },
  });
}
